import React, { useRef, useEffect, useMemo, useCallback } from "react";
import { Container, Graphics, Sprite, Text } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import * as PIXI from "pixi.js";
import { TextMetrics } from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { closeBonusBuy } from "../../../../State/actions";
import { selectBonusBuyActive } from "../../../../State/selectors";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { ASSET_NAME } from "../../../../assets";
import ButtonMenuClose from "./ButtonMenuClose";
import {
  createButtonPressAnimation,
  formatToCustomLocale,
} from "../../../../utilities";
import { SOUND_NAME } from "../../../../sounds";
import { soundManager } from "../../../../SoundManager";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import anime from "animejs";
import BonusBetSelector from "../Displays/BonusBetSelector";
import {
  selectCounterAnimationFinished,
  selectIsSpinning,
  selectCriticalAnimationPlaying,
  selectBonusBuyMultiplier,
  selectCurrency,
  selectBalance,
} from "../../../../State/selectors";

// Buy Bonus popup configuration
//
// HOW SCALING WORKS:
// 1. All content (text, images, buttons) scales uniformly based on popup size
// 2. Popup size = BASE_SIZE_RATIO × deviceMultiplier × scaleMultiplier × GLOBAL_SCALE
// 3. Content scales relative to popup width (referencePopupWidth = 800px)
// 4. This ensures identical proportions across all devices - only overall size changes
//
// TO ADJUST SIZES:
// - GLOBAL_SCALE: Scale everything uniformly across all devices
// - scaleMultiplier: Fine-tune individual device/orientation sizes
// - BASE_SIZE_RATIO: Change base popup size (affects all devices)
export const POPUP_BONUS_CONFIG = {
  // GLOBAL MULTIPLIER - Scale everything uniformly across all devices
  GLOBAL_SCALE: 1.0, // Increased from 1.0 to 1.1 (10% bigger overall)

  // Base size (maximum size as % of screen - single value controls both dimensions)
  BASE_SIZE_RATIO: 0.99, // 99% of screen size (before device scaling)

  // Button bottom padding (multiplier of popup height)
  BUTTON_BOTTOM_PADDING_SYMBOL_HEIGHT: 0.0,

  // Base values in pixels (scaled relative to popup size via referencePopupWidth = 800px)
  // All devices use these same base values for uniform proportions
  basePadding: {
    horizontal: 80,
    vertical: 60, // Increased from 36 for more vertical padding
  },
  baseTitleFontSize: 48,
  baseTitleGap: 32, // Increased from 24 for more spacing
  baseSubtitleFontSize: 36,
  baseSubtitleGap: 28, // Increased from 20 for more spacing
  baseBetValueFontSize: 36,
  baseBetValueGap: 32, // Increased from 24 for more spacing

  // Default values applied to all devices (can be overridden per device/orientation)
  DEFAULTS: {
    deviceMultiplier: 1.0,
    paddingMultiplier: { h: 1.0, v: 1.0 },
    titleFontMultiplier: 1.0,
    titleGapMultiplier: 1.0,
    subtitleFontMultiplier: 1.0,
    subtitleGapMultiplier: 1.0,
    betValueFontMultiplier: 1.0,
    betValueGapMultiplier: 1.0,
    closeButtonOffsetX: 0.0,
    closeButtonOffsetY: -0.35,
    popupVerticalOffset: 0,
    betSelectorVerticalOffset: 0.15, // Distance from popup as percentage of popup height
  },

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        scaleMultiplier: 0.75,
        closeButtonOffsetX: 0.1, // Adjust: positive = right, negative = left
        closeButtonOffsetY: -0.32, // Adjust: positive = down, negative = up
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.6,
        popupVerticalOffset: 0.1,
        closeButtonOffsetX: 0.3, // Adjust: positive = right, negative = left
        closeButtonOffsetY: -0.075, // Adjust: positive = down, negative = up
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        scaleMultiplier: 0.45,
        closeButtonOffsetX: 0.2, // Adjust: positive = right, negative = left
        closeButtonOffsetY: -0.35, // Adjust: positive = down, negative = up
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        scaleMultiplier: 0.45,
        closeButtonOffsetX: 0.2, // Adjust: positive = right, negative = left
        closeButtonOffsetY: -0.25, // Adjust: positive = down, negative = up
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      scaleMultiplier: 0.5, // Increased from 0.5 to match mobile landscape appearance
      closeButtonOffsetX: 0.2, // Adjust: positive = right, negative = left
      closeButtonOffsetY: -0.25, // Adjust: positive = down, negative = up
    },
  },
};

const BuyBonusButton = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const popiBonusActive = useSelector(selectBonusBuyActive);
  const { handleSpin } = useReelControl();
  // Guards - prevent starting new spin if ongoing spin, win lines displaying, or critical animation
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const spinComplete = useSelector((state) => state.spin.spinComplete);
  const currentRound = useSelector((state) => state.game.currentRound);

  const handleClose = () => {
    dispatch(closeBonusBuy());
  };

  const { dimensions, getResponsiveConfig } = useDimensions();
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  // Get current bet, currency, and balance for displaying bonus buy cost and checking affordability
  const currentBet = useSelector((state) => state.game.bet);
  const currency = useSelector(selectCurrency);
  const bonusBuyMultiplier = useSelector(selectBonusBuyMultiplier);
  const balance = useSelector(selectBalance);

  // Calculate bonus cost in real-time (recalculates when bet or multiplier changes)
  const bonusCost = currentBet * bonusBuyMultiplier;

  // Check if user can afford the bonus buy (real-time)
  const canAffordBonus = balance >= bonusCost;

  // Refs for BUY button hover and click effects
  const buyButtonSpriteRef = useRef(null);
  const isHoveredRef = useRef(false);
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  const handleBuy = useCallback(() => {
    // Calculate fresh bonusCost at the time of click to ensure we use the latest bet value
    const currentBonusCost = currentBet * bonusBuyMultiplier;

    // Prevent if user can't afford the bonus
    if (balance < currentBonusCost) {
      return;
    }

    // Prevent starting new spin if ongoing spin, win lines displaying, or critical animation
    const hasWin = currentRound?.win && currentRound.win > 0;
    const winNotFullyDisplayed =
      hasWin && spinComplete && !counterAnimationFinished;

    if (isSpinning || winNotFullyDisplayed || criticalAnimationPlaying) {
      return;
    }

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Add press animation before handling buy
    if (buyButtonSpriteRef.current) {
      createButtonPressAnimation(buyButtonSpriteRef.current);
    }

    // Dispatch custom event to trigger button active state immediately
    window.dispatchEvent(new CustomEvent("bonusBuyTriggered"));

    // Close popup
    dispatch(closeBonusBuy());

    // Trigger spin with purchased_feature option and bonusCost for proper balance deduction
    // Use currentBonusCost calculated at click time to ensure latest bet value is used
    handleSpin({ purchasedFeature: "bonus_buy", bonusCost: currentBonusCost });

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  }, [
    dispatch,
    handleSpin,
    isSpinning,
    criticalAnimationPlaying,
    currentRound,
    spinComplete,
    counterAnimationFinished,
    currentBet,
    bonusBuyMultiplier,
    balance,
  ]);

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Don't apply hover effect if button is disabled
    if (!canAffordBonus) {
      return;
    }

    if (buyButtonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buyButtonSpriteRef.current.filters ||
        buyButtonSpriteRef.current.filters.length === 0
      ) {
        buyButtonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [canAffordBonus]);

  const handlePointerOut = useCallback(() => {
    if (buyButtonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buyButtonSpriteRef.current.filters = [];
    }
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      if (popiBonusActive) {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          easing: "easeOutQuad",
        });
      } else {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 0,
          duration: 200,
          easing: "easeInQuad",
        });
      }
    }
  }, [popiBonusActive]);

  // Clean up hover effects and debounce timeout on unmount
  useEffect(() => {
    const sprite = buyButtonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
      // Clear any pending debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Get popup frame texture
  const popupFrameTexture = PIXI.Assets.get(ASSET_NAME.popup_frame_bonus);

  // Get responsive popup configuration
  const popupConfig = useMemo(() => {
    return getResponsiveConfig(POPUP_BONUS_CONFIG);
  }, [getResponsiveConfig]);

  // Calculate popup size: maximum size with device-specific multipliers
  const popupSize = useMemo(() => {
    if (!popupFrameTexture) {
      return { width: 0, height: 0 };
    }

    // Get native texture dimensions
    const nativeWidth = popupFrameTexture.width;
    const nativeHeight = popupFrameTexture.height;
    const aspectRatio = nativeWidth / nativeHeight;

    // Calculate maximum size (single ratio applied to both dimensions)
    const maxSize =
      Math.min(dimensions.width, dimensions.height) *
      POPUP_BONUS_CONFIG.BASE_SIZE_RATIO;
    const maxWidth = maxSize;
    const maxHeight = maxSize;

    // Calculate size based on aspect ratio to fit within max dimensions
    let width, height;
    if (maxWidth / aspectRatio <= maxHeight) {
      // Width is the limiting factor
      width = maxWidth;
      height = maxWidth / aspectRatio;
    } else {
      // Height is the limiting factor
      height = maxHeight;
      width = maxHeight * aspectRatio;
    }

    // Apply device-specific multiplier, device scale, AND global scale
    const deviceScale = popupConfig.scaleMultiplier || 1.0;
    const finalScale = POPUP_BONUS_CONFIG.GLOBAL_SCALE * deviceScale;
    width *= popupConfig.deviceMultiplier * finalScale;
    height *= popupConfig.deviceMultiplier * finalScale;

    // Cap at native texture size (don't exceed original texture dimensions)
    if (width > nativeWidth) {
      width = nativeWidth;
      height = nativeWidth / aspectRatio;
    }
    if (height > nativeHeight) {
      height = nativeHeight;
      width = nativeHeight * aspectRatio;
    }

    return { width, height };
  }, [
    popupFrameTexture,
    dimensions,
    popupConfig.deviceMultiplier,
    popupConfig.scaleMultiplier,
  ]);

  // Calculate close button position (outside popup, configurable per device)
  const closeButtonPosition = useMemo(() => {
    if (!popupSize || popupSize.width === 0) {
      return { x: 0, y: 0 };
    }

    // Use configurable offsets from popupConfig
    const offsetX = popupSize.width * popupConfig.closeButtonOffsetX;
    const offsetY = popupSize.height * popupConfig.closeButtonOffsetY;

    return {
      x: Math.round(dimensions.width / 2 + popupSize.width / 2 + offsetX),
      y: Math.round(dimensions.height / 2 - popupSize.height / 2 + offsetY),
    };
  }, [popupSize, popupConfig, dimensions]);

  // Calculate viewport configuration (same as PopiAutoplayPopup)
  const viewportConfig = useMemo(() => {
    if (!popupFrameTexture || popupSize.width === 0) return null;

    const config = popupConfig;

    // Calculate scale based on popup size relative to a reference size
    // This ensures everything scales uniformly with the popup
    const referencePopupWidth = 800; // Reference width for base values
    const popupScale = popupSize.width / referencePopupWidth;

    const paddingHorizontal =
      POPUP_BONUS_CONFIG.basePadding.horizontal *
      config.paddingMultiplier.h *
      popupScale;
    const paddingVertical =
      POPUP_BONUS_CONFIG.basePadding.vertical *
      config.paddingMultiplier.v *
      popupScale;

    const contentWidth = popupSize.width - paddingHorizontal * 2;
    const contentHeight = popupSize.height - paddingVertical * 2;
    const titleFontSize = Math.round(
      POPUP_BONUS_CONFIG.baseTitleFontSize *
        config.titleFontMultiplier *
        popupScale,
    );
    const titleGap =
      POPUP_BONUS_CONFIG.baseTitleGap * config.titleGapMultiplier * popupScale;
    const subtitleFontSize = Math.round(
      POPUP_BONUS_CONFIG.baseSubtitleFontSize *
        config.subtitleFontMultiplier *
        popupScale,
    );
    const subtitleGap =
      POPUP_BONUS_CONFIG.baseSubtitleGap *
      config.subtitleGapMultiplier *
      popupScale;
    const betValueFontSize = Math.round(
      POPUP_BONUS_CONFIG.baseBetValueFontSize *
        config.betValueFontMultiplier *
        popupScale,
    );
    const betValueGap =
      POPUP_BONUS_CONFIG.baseBetValueGap *
      config.betValueGapMultiplier *
      popupScale;

    const titleYOffset = titleFontSize + titleGap;
    const subtitleYOffset = titleYOffset + subtitleFontSize + subtitleGap;

    return {
      maxWidth: contentWidth,
      maxHeight: contentHeight,
      x: -popupSize.width / 2 + paddingHorizontal,
      y: -popupSize.height / 2 + paddingVertical,
      titleFontSize,
      titleYOffset,
      subtitleFontSize,
      subtitleGap,
      subtitleYOffset,
      betValueFontSize,
      betValueGap,
      viewportHeight: contentHeight - titleYOffset,
    };
  }, [popupConfig, popupSize, popupFrameTexture]);

  // Text styles
  const titleStyle = useMemo(
    () =>
      viewportConfig &&
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: viewportConfig.titleFontSize,
        fontWeight: "bold",
        fill: 0xffffff, // White color
        align: "center",
      }),
    [viewportConfig],
  );

  // Get subtitle text for auto-scaling calculation
  const subtitleText = t("buy_bonus_game_01_desc");

  // Auto-scaling font size calculation for subtitle (max 4 rows with padding)
  const finalSubtitleFontSize = useMemo(() => {
    if (!viewportConfig) return 0;

    const MAX_ROWS = 4; // Maximum number of text rows allowed (increased from 3 to 4)
    const MIN_FONT_SIZE = 12; // Minimum readable font size
    const textPadding = viewportConfig.maxWidth * 0.05; // 5% padding on each side
    const maxTextWidth = viewportConfig.maxWidth - textPadding * 2; // Respect padding
    let fontSize = viewportConfig.subtitleFontSize;
    let iterations = 0;
    const maxIterations = 20;

    while (iterations < maxIterations) {
      // Create test style with current font size
      const testStyle = new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: fontSize,
        fontWeight: "400",
        wordWrap: true,
        wordWrapWidth: maxTextWidth,
        breakWords: true, // Force break for Asian languages
        lineHeight: Math.round(fontSize * 1.1), // Compact line height
      });

      const metrics = TextMetrics.measureText(subtitleText, testStyle);
      const lineHeight = metrics.lineHeight || fontSize * 1.1;
      const actualRows = Math.ceil(metrics.height / lineHeight);

      // Check if text fits within max rows
      if (actualRows <= MAX_ROWS) {
        break;
      }

      // Check if we've hit minimum font size
      if (fontSize <= MIN_FONT_SIZE) {
        break; // Stop scaling to maintain readability
      }

      // Scale down by 5% and try again
      fontSize = Math.round(fontSize * 0.95);
      iterations++;
    }

    return fontSize;
  }, [viewportConfig, subtitleText]);

  const subtitleStyle = useMemo(() => {
    if (!viewportConfig || finalSubtitleFontSize <= 0) return null;

    const textPadding = viewportConfig.maxWidth * 0.05; // 5% padding on each side
    const maxTextWidth = viewportConfig.maxWidth - textPadding * 2;

    return new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: finalSubtitleFontSize,
      fontWeight: "600", // Changed from "400" to "600" for bold
      fill: 0xffffff, // White color
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxTextWidth, // Use width with padding
      breakWords: true, // Force break for Asian languages
      lineHeight: Math.round(finalSubtitleFontSize * 1.1), // Compact line height
    });
  }, [viewportConfig, finalSubtitleFontSize]);

  const betValueStyle = useMemo(
    () =>
      viewportConfig &&
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: viewportConfig.betValueFontSize,
        fontWeight: "bold",
        fill: 0xffffff, // White color
        align: "center",
      }),
    [viewportConfig],
  );

  // Get button background texture (Buy Bonus specific)
  const buttonBackgroundTexture = PIXI.Assets.get(
    ASSET_NAME.button_buy_bonus_background,
  );

  // Get buy bonus feature image texture
  const buyBonusFeatureTexture = PIXI.Assets.get(ASSET_NAME.buy_bonus_feature);

  // Calculate button configuration with price display
  const buyButtonConfig = useMemo(() => {
    if (!viewportConfig || !popupConfig) return null;

    // Calculate button size - wider and taller for two-line content (BUY + price)
    const baseInputHeight = viewportConfig.titleFontSize * 0.8;
    const baseInputWidth = baseInputHeight * 2.25;

    // Increased size for wider, taller button with two lines of text
    const buttonHeight = baseInputHeight * 3.5; // Reduced from 4.0
    const buttonWidth = baseInputWidth * 4.5; // Reduced from 5.0

    // BUY text font size (top line)
    const buyFontSize = Math.round(buttonHeight * 0.28); // 28% of button height (reduced from 35%)

    // Price text font size (bottom line) - same as BUY text
    const priceFontSize = buyFontSize; // Same size as BUY text

    // Calculate bonus buy cost (bonusBuyMultiplier x bet) - formatted like BalanceDisplay
    const calculatedBonusCost = currentBet * bonusBuyMultiplier;
    const formattedBonusCost = formatToCustomLocale(calculatedBonusCost);
    const priceText = `${formattedBonusCost} ${currency}`;

    return {
      width: buttonWidth,
      height: buttonHeight,
      buyFontSize,
      priceFontSize,
      buyStyle: new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: buyFontSize,
        fontWeight: "bold",
        fill: 0x000000, // Black text
        align: "center",
      }),
      priceStyle: new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: priceFontSize,
        fontWeight: "bold",
        fill: 0x000000, // Black text with opacity applied to Text component
        align: "center",
      }),
      priceText,
    };
  }, [viewportConfig, popupConfig, currentBet, bonusBuyMultiplier, currency]);

  const betSelectorConfig = useMemo(() => {
    if (!viewportConfig || !popupSize) return null;
    // Calculate scale based on popup size (same as viewport content)
    const referencePopupWidth = 800;
    const popupScale = popupSize.width / referencePopupWidth;
    return {
      popupScale, // Pass popup-relative scale to BonusBetSelector
    };
  }, [viewportConfig, popupSize]);

  const betSelectorPosition = useMemo(() => {
    if (!popupSize || !viewportConfig || !popupConfig) return { x: 0, y: 0 };
    // Use device-specific offset above the popup frame
    const offset =
      popupSize.height * (popupConfig.betSelectorVerticalOffset || 0.25);
    // Account for popup vertical offset
    const popupVerticalShift =
      dimensions.height * (popupConfig.popupVerticalOffset || 0);
    return {
      x: Math.round(dimensions.width / 2),
      y: Math.round(
        dimensions.height / 2 +
          popupVerticalShift -
          popupSize.height / 2 -
          offset,
      ),
    };
  }, [popupSize, viewportConfig, popupConfig, dimensions]);

  // Calculate buy bonus feature image dimensions and position
  const featureImageConfig = useMemo(() => {
    if (
      !viewportConfig ||
      !buyBonusFeatureTexture ||
      !buyButtonConfig ||
      !finalSubtitleFontSize
    ) {
      return null;
    }

    // Step 1: Calculate where subtitle text actually ends
    const subtitleStartY = viewportConfig.titleYOffset; // After title + gap
    const maxTextWidth = viewportConfig.maxWidth * 1.2;
    const subtitleTextStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: finalSubtitleFontSize,
      fontWeight: "400",
      wordWrap: true,
      wordWrapWidth: maxTextWidth,
    });
    const subtitleMetrics = TextMetrics.measureText(
      subtitleText,
      subtitleTextStyle,
    );
    const actualSubtitleHeight = subtitleMetrics.height;

    // Subtitle ends at: start position + actual text height
    const subtitleEndY = subtitleStartY + actualSubtitleHeight;

    // Step 2: Add configurable gap after subtitle before image starts (uses subtitleGap from config)
    const imageStartY = subtitleEndY + viewportConfig.subtitleGap;

    // Step 3: Calculate where button and bet value are positioned (working from bottom up)
    // Use popup height for padding instead of uiSymbolHeight to maintain uniform scaling
    const buttonPadding =
      POPUP_BONUS_CONFIG.BUTTON_BOTTOM_PADDING_SYMBOL_HEIGHT * popupSize.height;
    const buttonBottomY = viewportConfig.maxHeight - buttonPadding;
    const buttonTopY = buttonBottomY - buyButtonConfig.height;

    const betValueHeight = viewportConfig.betValueFontSize;
    const betValueBottomY = buttonTopY - viewportConfig.betValueGap;
    const betValueTopY = betValueBottomY - betValueHeight;

    // Step 4: Image ends where bet value gap starts (no additional gap needed)
    // The betValueGap already provides spacing between image and bet value
    const imageEndY = betValueTopY;

    // Step 5: Calculate available space for image
    const availableHeight = imageEndY - imageStartY;
    const availableWidth = viewportConfig.maxWidth * 0.85; // Reduced from 1.2 to 0.85 (85% of viewport width)

    // Ensure we have positive space
    if (availableHeight <= 0) {
      return null;
    }

    // Step 6: Scale image to fit available space while maintaining aspect ratio
    const nativeWidth = buyBonusFeatureTexture.width;
    const nativeHeight = buyBonusFeatureTexture.height;
    const aspectRatio = nativeWidth / nativeHeight;

    let scaledWidth, scaledHeight;

    // Calculate both possible sizes
    const heightBasedWidth = availableHeight * aspectRatio;
    const widthBasedHeight = availableWidth / aspectRatio;

    // Use whichever fits within available space
    if (heightBasedWidth <= availableWidth) {
      // Height is limiting factor - use full available height
      scaledHeight = availableHeight;
      scaledWidth = heightBasedWidth;
    } else {
      // Width is limiting factor - use full available width
      scaledWidth = availableWidth;
      scaledHeight = widthBasedHeight;
    }

    // Step 7: Center image vertically in available space
    const imageY = imageStartY + availableHeight / 2;

    return {
      width: scaledWidth,
      height: scaledHeight,
      x: Math.round(viewportConfig.maxWidth / 2),
      y: Math.round(imageY),
    };
  }, [
    viewportConfig,
    buyBonusFeatureTexture,
    buyButtonConfig,
    popupSize,
    finalSubtitleFontSize,
    subtitleText,
  ]);

  // Don't render if texture not loaded or viewport config not ready
  if (!popupFrameTexture || !viewportConfig || !titleStyle) {
    return null;
  }

  return (
    <Container
      ref={containerRef}
      name="secondary_bonus_overlay"
      zIndex={popiBonusActive ? 10 : 0}
      eventMode={popiBonusActive ? "static" : "none"}
    >
      {/* Black Background with 0.8 opacity */}
      <Graphics
        name="secondary_bonus_background"
        draw={(g) => {
          g.clear();
          g.beginFill(0x000000, 1);
          g.drawRect(0, 0, dimensions.width, dimensions.height);
          g.endFill();
        }}
        alpha={0.8}
        eventMode={popiBonusActive ? "static" : "none"}
        onpointertap={(e) => e.stopPropagation()}
      />

      {/* Bonus Popup Frame - Centered with device-specific vertical offset */}
      <Container
        name="secondary_bonus_popup"
        x={Math.round(dimensions.width / 2)}
        y={Math.round(
          dimensions.height / 2 +
            dimensions.height * (popupConfig.popupVerticalOffset || 0),
        )}
      >
        {/* Popup Frame Sprite - centered, maintains aspect ratio */}
        <Sprite
          texture={popupFrameTexture}
          anchor={0.5}
          width={popupSize.width}
          height={popupSize.height}
        />

        {/* Content Viewport Container - positioned relative to parent container */}
        <Container
          name="secondary_bonus_content_viewport"
          x={Math.round(viewportConfig.x)}
          y={Math.round(viewportConfig.y)}
        >
          {/* Bonus Title */}
          <Text
            text={t("buy_bonus_game_01").toUpperCase()}
            style={titleStyle}
            anchor={{ x: 0.5, y: 0 }}
            x={Math.round(viewportConfig.maxWidth / 2)}
            y={0}
            resolution={2}
          />

          {/* Bonus Subtitle */}
          {subtitleStyle && (
            <Text
              text={subtitleText}
              style={subtitleStyle}
              anchor={{ x: 0.5, y: 0 }}
              x={Math.round(viewportConfig.maxWidth / 2)}
              y={Math.round(viewportConfig.titleYOffset)}
              resolution={2}
              alpha={0.5}
            />
          )}

          {/* Buy Bonus Feature Image - centered between subtitle and bet value */}
          {featureImageConfig && buyBonusFeatureTexture && (
            <Sprite
              texture={buyBonusFeatureTexture}
              anchor={0.5}
              width={featureImageConfig.width}
              height={featureImageConfig.height}
              x={featureImageConfig.x}
              y={featureImageConfig.y}
            />
          )}

          {/* Bet Value - above button */}
          {betValueStyle && currentBet && (
            <Text
              text={t("times_bet").replace("{count}", bonusBuyMultiplier)}
              style={betValueStyle}
              anchor={{ x: 0.5, y: 1 }}
              x={Math.round(viewportConfig.maxWidth / 2)}
              y={Math.round(
                viewportConfig.maxHeight -
                  buyButtonConfig.height -
                  POPUP_BONUS_CONFIG.BUTTON_BOTTOM_PADDING_SYMBOL_HEIGHT *
                    popupSize.height -
                  viewportConfig.betValueGap,
              )}
              resolution={2}
            />
          )}

          {/* BUY Button - centered at bottom with symbol-height-based padding */}
          {buyButtonConfig && buttonBackgroundTexture && (
            <Container
              x={Math.round(viewportConfig.maxWidth / 2)}
              y={Math.round(
                viewportConfig.maxHeight -
                  buyButtonConfig.height / 2 -
                  POPUP_BONUS_CONFIG.BUTTON_BOTTOM_PADDING_SYMBOL_HEIGHT *
                    popupSize.height,
              )}
              eventMode={canAffordBonus ? "static" : "none"}
              cursor={canAffordBonus ? "pointer" : "default"}
              onpointertap={handleBuy}
              onpointerover={handlePointerOver}
              onpointerout={handlePointerOut}
              alpha={canAffordBonus ? 1 : 0.5}
            >
              {/* Button Background */}
              <Sprite
                ref={buyButtonSpriteRef}
                texture={buttonBackgroundTexture}
                anchor={0.5}
                width={buyButtonConfig.width}
                height={buyButtonConfig.height}
              />
              {/* BUY Text (top line) */}
              <Text
                text={t("common:buy").toUpperCase()}
                style={buyButtonConfig.buyStyle}
                anchor={{ x: 0.5, y: 1 }}
                x={0}
                y={-buyButtonConfig.height * 0.02}
                resolution={2}
              />
              {/* Price Text (bottom line) */}
              <Text
                text={buyButtonConfig.priceText}
                style={buyButtonConfig.priceStyle}
                anchor={{ x: 0.5, y: 0 }}
                x={0}
                y={buyButtonConfig.height * 0.02}
                resolution={2}
                alpha={0.6}
              />
            </Container>
          )}
        </Container>
      </Container>

      {/* Bonus Bet Selector - positioned above popup */}
      {betSelectorConfig && (
        <BonusBetSelector
          popupScale={betSelectorConfig.popupScale}
          position={betSelectorPosition}
        />
      )}

      {/* Close Button - positioned outside popup on top right */}
      <ButtonMenuClose
        onClose={handleClose}
        x={closeButtonPosition.x}
        y={closeButtonPosition.y}
        isSettings={true}
        sizeMultiplier={0.65}
      />
    </Container>
  );
};

export default BuyBonusButton;
